/**
 * The multi-select popover body: search, Select all / Deselect all, then one
 * checkbox row per option (the option's own rich content — brand dots, flags,
 * tag colours — is reused verbatim from SearchableSelect's vocabulary).
 * For Media Devoted | Created By James Alexandrov
 */

import { useMemo, useRef, type FC } from 'react';
import { Check, Search } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { SearchableSelectOption } from '../SearchableSelect';
import { useScrollIsolation } from '../useScrollIsolation';
import { MultiSelectBulkActions } from './MultiSelectBulkActions';

interface Props {
  options: SearchableSelectOption<string>[];
  value: string[];
  onChange: (value: string[]) => void;
  search: string;
  onSearch: (v: string) => void;
  searchPlaceholder: string;
}

export const MultiSelectPanel: FC<Props> = ({
  options,
  value,
  onChange,
  search,
  onSearch,
  searchPlaceholder,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  // The popover is portalled to the body, outside a host Dialog's scroll lock.
  const setListRef = useScrollIsolation(listRef);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, search]);

  const toggle = (v: string) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <>
      <div className="border-b border-border p-2">
        <div className="flex min-w-0 items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            type="search"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
            autoComplete="off"
          />
        </div>
      </div>
      {/* Deselect all with no search IS the "back to everything" reset — an
          empty selection is what this control means by "all". */}
      <MultiSelectBulkActions
        shown={filtered}
        value={value}
        onChange={onChange}
        searching={!!search.trim()}
      />
      <div
        ref={setListRef}
        role="listbox"
        aria-multiselectable
        className="max-h-[320px] overflow-y-auto p-2"
      >
        {filtered.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {options.length === 0 ? 'No options' : 'No matches'}
          </p>
        ) : (
          filtered.map((opt) => {
            const on = value.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => toggle(opt.value)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors',
                  'hover:bg-accent hover:text-accent-foreground',
                  on && 'bg-accent/60',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    on
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input',
                  )}
                  aria-hidden
                >
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0 truncate">
                  {opt.optionContent ?? opt.label}
                </span>
              </button>
            );
          })
        )}
      </div>
    </>
  );
};

export default MultiSelectPanel;
